import { BaseModule } from '@core/base/module/base-module'
import { IMdrState, TCase, TMdrSearchableTabs } from './mdr'
import { createUnifiedMdrConfig } from './mdr.filters'
import { filterByTimeRange, getShowDemoFromStorage, MDR_SHOW_DEMO_KEY } from './utils'
import { UnifiedFilterModule } from '@core/modules/filters/unified-filter.module'
import { ASM_DIALOGS_MAP, DialogService } from '@/core/services/dialog'
import { Analytics } from '@/core/services/mixpanel'

const incidentsFilterModule = new UnifiedFilterModule(
    createUnifiedMdrConfig('incidents'),
    'mdr.incidents.filter'
)

const alertsFilterModule = new UnifiedFilterModule(
    createUnifiedMdrConfig('alerts'),
    'mdr.alerts.filter'
)

const serviceRequestsFilterModule = new UnifiedFilterModule(
    createUnifiedMdrConfig('serviceRequests'),
    'mdr.serviceRequests.filter'
)

const defaultTimeRange = { id: 'all', label: 'All Times', checked: true }

const state = {
    showDemoByAccount: getShowDemoFromStorage(),
    timeRange: defaultTimeRange,
} satisfies IMdrState

class Mdr extends BaseModule<IMdrState> {
    private filterModules: Record<TMdrSearchableTabs, UnifiedFilterModule> = {
        incidents: incidentsFilterModule,
        alerts: alertsFilterModule,
        serviceRequests: serviceRequestsFilterModule,
    }

    constructor(state: IMdrState) {
        super(state, 'mdr.module')
        this.store.setKey('showDemoByAccount', getShowDemoFromStorage())
    }

    getFilterModulesMap(): Record<TMdrSearchableTabs, UnifiedFilterModule> {
        return this.filterModules
    }

    getShowDemo(): boolean {
        const showDemoByAccount = this.useStore().showDemoByAccount
        return showDemoByAccount?.[this.accountId] ?? false
    }

    setShowDemo(value: boolean): void {
        const accountId = this.accountId
        if (!accountId) return
        const showDemoByAccount = {
            ...(this.store.get().showDemoByAccount ?? {}),
        }
        showDemoByAccount[accountId] = value
        this.store.setKey('showDemoByAccount', showDemoByAccount)
        localStorage.setItem(
            MDR_SHOW_DEMO_KEY,
            JSON.stringify(showDemoByAccount)
        )
    }

    getTimeRange() {
        return this.useStore().timeRange ?? defaultTimeRange
    }

    setTimeRange(timeRange: { id: string; label: string; checked?: boolean }) {
        this.store.setKey('timeRange', timeRange)
    }

    getFilterModuleForTab(tab: TMdrSearchableTabs): UnifiedFilterModule {
        return this.filterModules[tab]
    }

    processCases(data: TCase, timeRange: { id: string; label: string; checked?: boolean }): TCase {
        return Object.fromEntries(
            Object.keys(data).map((key) => {
                const tabKey = key as TMdrSearchableTabs
                const originalData = data[tabKey]
                if (timeRange.id === 'all') {
                    return [key, originalData]
                }
                return [key, filterByTimeRange(originalData, timeRange)]
            })
        ) as TCase
    }

    openScheduleCallDialog(params: { isMdrActive: boolean; origin: string }) {
        const { isMdrActive, origin } = params ?? {}

        Analytics.event('SEM::mdr_schedule_a_call_clicked', {
            isMdrActive,
            origin,
        })
        DialogService.showDialog(ASM_DIALOGS_MAP.SCHEDULE_CALL)
    }

    openMdrVideoDialog(params: { isMdrActive: boolean; origin: string }) {
        const { isMdrActive, origin } = params ?? {}

        Analytics.event('SEM::mdr_see_why_video_modal_open', {
            isMdrActive,
            origin,
        })
        DialogService.showDialog(ASM_DIALOGS_MAP.MDR_VIDEO)
    }
}

export const MdrModule = new Mdr(state)
export type TMdrModule = typeof MdrModule
